import { ActivityIndicator, FlatList, SafeAreaView, ScrollView, SxProp, useDripsyTheme, View } from 'dripsy';
import ReactNative from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useContext, useRef, useState } from 'react';

import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs/UniInput';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { NetAssetValue } from '../../components/cards/NetAssetValue';
import { OverviewProps } from './overview/Overview';
import { PositionsProps } from '../../components/positions/Positions.web';
import { RootStackRoutes } from 'types';
import { EarnStackRoutes, EarnTabRoutes } from '../../earn.navigator';
import { keyGenerator } from '@/utils/keyGenerator';
import { MyEarningsProps } from './myEarnings/MyEarnings';
import { Analysis } from './Analysis.modal';
import { PortfolioNativeItem } from './PortfolioNativeItem';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store/store';
import { Footer } from '@/design-system/components/footer/Footer';
import { EarnContext } from '../../contexts/useEarnContext';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';

interface PortfolioNativeProps {
  overviewProps: OverviewProps;
  positionsProps: PositionsProps;
  myEarningsProps: MyEarningsProps;
  refreshPortfolio: () => void;
}

export const PortfolioNative: React.FC<PortfolioNativeProps> = ({ overviewProps, positionsProps, myEarningsProps, refreshPortfolio }) => {
  const { value: showAmount, set: setShowAmount } = useAsyncStorage<boolean>('showAmount');

  const { t } = useTranslation('earn');
  const { theme } = useDripsyTheme();
  const navigation = useNavigation();

  const [viewAnalysis, setViewAnalysis] = useState(false);

  const scrollViewRef = useRef<ReactNative.ScrollView>(null);

  const toggleShowAmount = (value: boolean) => {
    setShowAmount(value);
    console.log('@toggleShowAmount', value);
  };

  return (
    <SafeAreaView sx={styles.container}>
      <ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={false}
        sx={{ flex: 1 }}
        contentContainerSx={{ flex: 1, gap: '$1' }}
        onScroll={(event) => {
          // if scroll to bottom, load more
          const THESHOLD = 20;
          if (
            event.nativeEvent.contentOffset.y >=
              event.nativeEvent.contentSize.height - event.nativeEvent.layoutMeasurement.height - THESHOLD &&
            !positionsProps.loading
          ) {
            positionsProps.paginatedProps.onNextPage();
          }
        }}
        scrollEventThrottle={16}>
        {/* Overview */}
        <View sx={styles.overview}>
          <View sx={styles.overviewHeader}>
            <TextTitle bold>{t('earn-invest::glossary.overview')}</TextTitle>
            <UniIconButton icon="navPortfolio" onPress={() => setViewAnalysis(true)} />
          </View>
          <View sx={styles.overviewSpace} />
          <NetAssetValue
            netAssetValue={overviewProps.netAssetValue || '0'}
            showAmount={!!showAmount}
            onChangeShowAmount={toggleShowAmount}
          />
          <View sx={styles.overviewSpace} />
          <View sx={styles.overviewInfo}>
            <View>
              <TextCaption semiBold secondary label>
                {t('earn-invest::glossary.mobile-yesterdays-earnings')}
              </TextCaption>
              <TextTitle semiBold success>
                {showAmount
                  ? formatAmountByDecimals({ value: myEarningsProps.overview?.$1d || 0, decimals: 2, suffix: 'USD' })
                  : '***************'}
              </TextTitle>
            </View>
            <View sx={{ alignItems: 'flex-end' }}>
              <TextCaption semiBold secondary label>
                {t('earn-invest::glossary.seven-day-earnings')}
              </TextCaption>
              <TextTitle semiBold success>
                {showAmount
                  ? formatAmountByDecimals({ value: myEarningsProps.overview?.$7d || 0, decimals: 2, suffix: 'USD' })
                  : '***************'}
              </TextTitle>
            </View>
          </View>
        </View>
        {/* Positions */}
        <View sx={styles.positions}>
          {/* Filter */}
          <View sx={{ paddingX: '$4' }}>
            <TextTitle bold>{t('earn-invest::glossary.positions')}</TextTitle>
            <View sx={{ height: '$5' }} />
            <UniInput
              size="medium"
              prefixIcon="search"
              placeholder={t('earn-invest::glossary.search-by-asset')}
              onValueChange={positionsProps.onFilterChange}
              wrapperSx={{ flexGrow: 1 }}
              sx={{ width: '100%' }}
            />
          </View>
          {/* Positions List */}
          <View sx={styles.positionsList}>
            {!positionsProps.loading && positionsProps.data.length === 0 && (
              <View sx={styles.positionsListEmpty}>
                <Icon name="zeroState" height={60} width={60} />
                <View sx={{ height: '$2' }} />
                <TextBody>{t('earn-invest::glossary.no-position-currently')}</TextBody>
                <View sx={{ height: '$5' }} />
                <UniButton
                  size="default"
                  label={t('earn-invest::glossary.discover')}
                  onPress={() => {
                    navigation.navigate(RootStackRoutes.Earn, {
                      screen: EarnStackRoutes.Main,
                      params: {
                        screen: EarnTabRoutes.Discover
                      }
                    });
                  }}
                  sx={{ paddingX: '$8' }}
                />
              </View>
            )}

            <FlatList
              data={positionsProps.data}
              keyExtractor={(_, index) => keyGenerator(`position-${index}`)}
              sx={{ paddingX: '$4' }}
              renderItem={({ item }) => (
                <PortfolioNativeItem
                  item={item}
                  positionsProps={{
                    ...positionsProps,
                    onUpdate: () => {
                      // scroll to top
                      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
                      positionsProps.onUpdate();
                    }
                  }}
                  refreshPortfolio={refreshPortfolio}
                />
              )}
              scrollEnabled={true}
              scrollEventThrottle={16}
            />

            {positionsProps.loading && (
              <View sx={{ padding: '$5' }}>
                <ActivityIndicator size="large" color={theme.colors?.$disable || '#F0F0F0'} />
              </View>
            )}
          </View>
        </View>
        {/* Footer */}
        <Footer />
        {/* Analysis Modal */}
        {viewAnalysis && <Analysis overviewProps={overviewProps} myEarningsProps={myEarningsProps} onBack={() => setViewAnalysis(false)} />}
      </ScrollView>
    </SafeAreaView>
  );
};

enum StyleName {
  container = 'container',
  overview = 'overview',
  overviewHeader = 'overviewHeader',
  overviewSpace = 'overviewSpace',
  overviewInfo = 'overviewInfo',
  positions = 'positions',
  positionsList = 'positionsList',
  positionsListEmpty = 'positionsListEmpty'
}

const styles: Record<StyleName, SxProp> = {
  container: { flex: 1, backgroundColor: '$backgroundSecondary' },
  overview: {
    borderTopLeftRadius: 15,
    borderTopRightRadius: 15,
    backgroundColor: '$backgroundPrimary',
    paddingX: '$4',
    paddingY: '$5'
  },
  overviewHeader: { flexDirection: 'row', justifyContent: 'space-between' },
  overviewSpace: { height: '$5' },
  overviewInfo: { flexDirection: 'row', justifyContent: 'space-between' },
  positions: { paddingY: '$5', backgroundColor: '$backgroundPrimary' },
  positionsList: { paddingY: '$5', pb: 0, backgroundColor: '$backgroundPrimary' },
  positionsListEmpty: { paddingX: '$4', flex: 1, alignItems: 'center', justifyContent: 'center' }
};
